/** Settings → Visit Reminders: supervisory / re-admission cycles and how many days ahead to remind. */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BellRing, CalendarClock, Loader2, Plus, Save, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VisitRemindersTab() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/assessments/schedule-settings`).then((r) => setS(r.data)).catch(() => toast.error("Could not load reminder settings"));
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setRow = (i, k, v) => setS((p) => ({ ...p, readmission_by_insurance: p.readmission_by_insurance.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/assessments/schedule-settings`, {
        supervisory_interval_days: Number(s.supervisory_interval_days), due_soon_days: Number(s.due_soon_days),
        readmission_default_days: Number(s.readmission_default_days),
        readmission_by_insurance: s.readmission_by_insurance.map((x) => ({ insurance_company: x.insurance_company, days: Number(x.days) })),
      });
      setS(r.data);
      toast.success("Visit reminder settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="visit-reminders-tab">
      <div className="form-section">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-rose-600" /> Supervisory visit cycle</h3>
        <p className="text-sm text-slate-500 mb-4">How often each active client needs an RN supervisory visit, and how far ahead the reminder should appear for the administrator and RN.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Supervisory visit every (days)</Label>
            <Input type="number" min={1} value={s.supervisory_interval_days} onChange={(e) => set("supervisory_interval_days", e.target.value)} data-testid="vr-supervisory-days" />
            <p className="text-[11px] text-slate-400 mt-1">Counted from the last completed supervisory visit (or the admission).</p>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><BellRing className="w-3 h-3" /> Remind to schedule (days before due)</Label>
            <Input type="number" min={0} value={s.due_soon_days} onChange={(e) => set("due_soon_days", e.target.value)} data-testid="vr-remind-days" />
            <p className="text-[11px] text-slate-400 mt-1">A "due within {s.due_soon_days || 0} days" alert shows on the dashboard, RN Assessments page and the client's Supervisory tab; it turns red once past due.</p>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3 className="text-lg font-semibold text-slate-800">Re-admission cycle</h3>
        <p className="text-sm text-slate-500 mb-4">Default re-admission timeframe, with overrides per insurance company (matched against the client's Insurance Company).</p>
        <div className="grid sm:grid-cols-2 gap-4 mb-3">
          <div>
            <Label className="text-xs">Re-admission every (days) — default</Label>
            <Input type="number" min={1} value={s.readmission_default_days} onChange={(e) => set("readmission_default_days", e.target.value)} data-testid="vr-readmission-days" />
          </div>
        </div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs">Per insurance company</Label>
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => set("readmission_by_insurance", [...s.readmission_by_insurance, { insurance_company: "", days: 180 }])} data-testid="vr-add-insurance">
            <Plus className="w-3.5 h-3.5" /> Add insurer
          </Button>
        </div>
        {s.readmission_by_insurance.length === 0 && <p className="text-xs text-slate-400">No overrides — every client uses the default.</p>}
        <div className="space-y-2">
          {s.readmission_by_insurance.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input placeholder="Insurance company" value={r.insurance_company} onChange={(e) => setRow(i, "insurance_company", e.target.value)} data-testid={`vr-insurance-name-${i}`} />
              <Input type="number" min={1} className="w-24" value={r.days} onChange={(e) => setRow(i, "days", e.target.value)} data-testid={`vr-insurance-days-${i}`} />
              <span className="text-xs text-slate-500">days</span>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600" onClick={() => set("readmission_by_insurance", s.readmission_by_insurance.filter((_, idx) => idx !== i))} data-testid={`vr-insurance-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="gap-1" data-testid="vr-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save reminder settings
        </Button>
      </div>
    </div>
  );
}
